import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 16px 64px;
  @media (min-width: 640px) { padding: 40px 24px 80px; }
`;

const Title = styled.h1`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 32px 0;
`;

const Section = styled.section`
  margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 12px 0;
`;

const Body = styled.div`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textSecondary};
  line-height: 1.8;

  p { margin: 0 0 10px 0; }
  ul { margin: 0 0 10px 0; padding-left: 20px; }
  li { margin-bottom: 4px; }
`;

const LastUpdated = styled.p`
  font-size: 0.75rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-top: 32px;
`;

function PrivacyPage() {
  const { t } = useLanguage();

  return (
    <Container>
      <Title>{t('プライバシーポリシー', 'Privacy Policy')}</Title>

      <Section>
        <SectionTitle>{t('1. 収集する個人情報', '1. Information We Collect')}</SectionTitle>
        <Body>
          <p>{t('本サービスでは、以下の個人情報を収集します。', 'We collect the following personal information:')}</p>
          <ul>
            <li>{t('アカウント情報（メールアドレス、名前、パスワードハッシュ）', 'Account information (email, name, hashed password)')}</li>
            <li>{t('Google認証連携時に取得するプロフィール情報', 'Profile information from Google authentication')}</li>
            <li>{t('診断の回答データおよび結果', 'Diagnosis responses and results')}</li>
            <li>{t('AIチャットでの会話内容（送信メッセージおよびAIの応答）', 'AI chat conversation content (your messages and the AI responses)')}</li>
            <li>{t('サービスの利用履歴・アクセスログ', 'Usage history and access logs')}</li>
            <li>{t('決済に関する情報（Stripeで処理。カード番号は当社では保持しません）', 'Payment information (processed by Stripe; we do not store card numbers)')}</li>
          </ul>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('2. 利用目的', '2. Purpose of Use')}</SectionTitle>
        <Body>
          <ul>
            <li>{t('サービスの提供・運営・改善', 'Providing, operating, and improving the Service')}</li>
            <li>{t('診断結果の生成・レポートの作成', 'Generating diagnosis results and reports')}</li>
            <li>{t('AIチャット機能の提供', 'Providing AI chat functionality')}</li>
            <li>{t('AIチャットの会話内容の保存、および応答品質・サービスの改善のための分析・活用', 'Storing AI chat conversations and analyzing them to improve response quality and the Service')}</li>
            <li>{t('お支払いの処理', 'Processing payments')}</li>
            <li>{t('お問い合わせ対応', 'Responding to inquiries')}</li>
            <li>{t('不正利用の防止', 'Preventing unauthorized use')}</li>
          </ul>
          <p>
            {t(
              'AIチャットでの会話内容は、サービス運営者がサービス向上・品質改善のために保存・閲覧・分析することがあります。会話内容を含む個人情報は、適切なセキュリティ対策のもとで保護し、本ポリシーに定める目的の範囲外では利用しません。',
              'AI chat conversations may be stored, reviewed, and analyzed by the Service operator for service improvement and quality assurance. Personal information including conversation content is protected with appropriate security measures and is not used beyond the purposes set out in this policy.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('3. 第三者提供', '3. Third-Party Disclosure')}</SectionTitle>
        <Body>
          <p>
            {t(
              '本サービスでは、決済処理のためStripe, Inc.にお客様の情報を提供します。それ以外の目的で、法令に基づく場合を除き、ユーザーの同意なく個人情報を第三者に提供することはありません。',
              'We share user information with Stripe, Inc. for payment processing. We do not disclose personal information to third parties without consent, except as required by law.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('4. Cookie・アクセス解析', '4. Cookies & Analytics')}</SectionTitle>
        <Body>
          <p>
            {t(
              '本サービスでは、認証トークンの保存にlocalStorageを使用します。サービスの改善を目的として、アクセス解析ツールを使用する場合があります。',
              'The Service uses localStorage for authentication tokens. We may use analytics tools to improve the Service.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('5. データの保管・セキュリティ', '5. Data Storage & Security')}</SectionTitle>
        <Body>
          <p>
            {t(
              '個人情報は適切なセキュリティ対策を講じた上で保管します。通信はSSL/TLSにより暗号化されています。パスワードはハッシュ化して保存し、平文では保持しません。',
              'Personal information is stored with appropriate security measures. Communications are encrypted via SSL/TLS. Passwords are stored as hashes, never in plaintext.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('6. ユーザーの権利', '6. User Rights')}</SectionTitle>
        <Body>
          <p>
            {t(
              'ユーザーは、自身の個人情報の開示・訂正・削除を求めることができます。ご希望の場合はお問い合わせ窓口までご連絡ください。',
              'Users may request disclosure, correction, or deletion of their personal information. Please contact us to make such requests.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('7. お問い合わせ', '7. Contact')}</SectionTitle>
        <Body>
          <p>info@workwrite.co.jp</p>
        </Body>
      </Section>

      <LastUpdated>{t('最終更新日: 2026年5月22日', 'Last updated: May 22, 2026')}</LastUpdated>
    </Container>
  );
}

export default PrivacyPage;
